import { createFileRoute, useNavigate, Link, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
 ArrowLeft, Loader2, Save, Trash2, ExternalLink, Crown, Star,
 Mail, CheckCircle2, AlertCircle, Download, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { sendClaimWhatsApp } from "@/server/listing-whatsapp-admin.functions";
import { CityZipInput } from "@/components/forms/CityZipInput";
import { SpecialtySelect } from "@/components/forms/SpecialtySelect";
import { AddressAutocomplete } from "@/components/forms/AddressAutocomplete";
import { MultiTagSelect } from "@/components/forms/MultiTagSelect";
import { SERVICES, LANGUAGES, INSURANCES, type Provider } from "@/data/heallexa";
import { loadListingFn, saveListingFn, deleteListingFn } from "./-dashboard.admin.listings.$id.functions";

export const Route = createFileRoute("/dashboard/admin/listings/$id")({
 component: AdminEditListing,
});

function exportEmailsCsv(
 rows: { sent_at: string; subject: string; email_type: string; status: string; email: string; error?: string | null }[],
 listingName: string,
) {
 const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
 const header = ["sent_at", "subject", "type", "status", "recipient", "error"].join(",");
 const body = rows
 .map((r) => [r.sent_at, r.subject, r.email_type, r.status, r.email, r.error ?? ""].map(esc).join(","))
 .join("\n");
 const blob = new Blob([header + "\n" + body], { type: "text/csv" });
 const url = URL.createObjectURL(blob);
 const a = document.createElement("a");
 a.href = url;
 a.download = `email-history-${listingName.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.csv`;
 a.click();
 URL.revokeObjectURL(url);
}

function AdminEditListing() {
 const { user, loading, primaryRole } = useAuth();
 const navigate = useNavigate();
 const { id } = useParams({ from: "/dashboard/admin/listings/$id" });
 const [provider, setProvider] = useState<Provider | null>(null);
 const [data, setData] = useState<any>(null);
 const [loaded, setLoaded] = useState(false);
 const [saving, setSaving] = useState(false);
 const [emails, setEmails] = useState<{ id: string; sent_at: string; subject: string; email_type: string; status: string; email: string; error?: string | null }[]>([]);

 useEffect(() => {
 if (loading) return;
 if (!user) { navigate({ to: "/login/admin" }); return; }
 if (primaryRole && primaryRole !== "admin") { navigate({ to: "/dashboard" }); return; }
 if (primaryRole !== "admin") return;
 (async () => {
 const { provider: p, logs, notifs } = await loadListingFn({ data: { id } });
 if (!p) { toast.error("Listing not found"); navigate({ to: "/dashboard/admin" }); return; }
 setProvider(p as Provider);
 setData({ ...p });
 const merged: any[] = [
 ...(logs ?? []).map((e: any) => ({
 id: e.id,
 sent_at: e.sent_at,
 subject: e.email_type,
 email_type: e.email_type,
 status: e.send_status,
 email: e.email,
 error: e.error_message,
 })),
 ...(notifs ?? []).map((n: any) => ({
 id: n.id,
 sent_at: n.sent_at ?? n.created_at,
 subject: n.subject ?? n.notification_type,
 email_type: n.notification_type,
 status: n.send_status,
 email: n.recipient_email,
 error: n.error_message,
 })),
 ].sort((a, b) => +new Date(b.sent_at) - +new Date(a.sent_at));
 setEmails(merged);
 setLoaded(true);
 })();
 }, [user, loading, primaryRole, id]);

 if (loading || !loaded || !data) {
 return <div className="min-h-screen flex items-center justify-center text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
 }

 const set = (k: string, v: any) => setData((d: any) => ({ ...d, [k]: v }));

 const save = async () => {
 setSaving(true);
 const { id: _id, created_at, updated_at, claim_url, ...payload } = data;
 try {
 await saveListingFn({ data: { id, payload } });
 toast.success("Listing updated");
 } catch (e: any) {
 toast.error(e?.message ?? "Failed to update listing");
 } finally {
 setSaving(false);
 }
 };

 const remove = async () => {
 if (!confirm("Delete this listing permanently?")) return;
 try {
 await deleteListingFn({ data: { id } });
 toast.success("Listing deleted");
 navigate({ to: "/dashboard/admin" });
 } catch (e: any) {
 toast.error(e?.message ?? "Failed to delete listing");
 }
 };

 const togglePremium = () => {
 const isPremium = data.listing_status === "premium";
 set("listing_status", isPremium ? "claimed" : "premium");
 set("plan", isPremium ? "free" : "premium_monthly");
 };

 return (
 <div>

 <header className="pt-28 pb-6 border-b border-border bg-card">
 <div className="mx-auto max-w-5xl px-6">
 <Link to="/dashboard/admin" className="text-xs text-muted-foreground inline-flex items-center gap-1 mb-2"><ArrowLeft className="h-3 w-3" /> Back to listings</Link>
 <div className="flex flex-wrap items-center justify-between gap-3">
 <div>
 <h1 className="text-3xl font-semibold tracking-tight">{data.practice}</h1>
 <p className="text-sm text-muted-foreground mt-1">{data.specialty} · {data.city}{data.state ? `, ${data.state}` : ""} · <span className="uppercase tracking-wider text-[10px]">{data.listing_status}</span></p>
 </div>
 <div className="flex flex-wrap gap-2">
 <Button variant="outline" size="sm" asChild><Link to="/providers/$id" params={{ id }}>View public <ExternalLink className="h-3 w-3" /></Link></Button>
 <Button variant={data.listing_status === "premium" ? "default" : "outline"} size="sm" onClick={togglePremium}><Crown className="h-3 w-3" /> {data.listing_status === "premium" ? "Premium" : "Make Premium"}</Button>
 <Button variant={data.is_featured ? "default" : "outline"} size="sm" onClick={() => set("is_featured", !data.is_featured)}><Star className="h-3 w-3" /> {data.is_featured ? "Featured" : "Feature"}</Button>
 <SendWhatsAppButton listingId={id} hasPhone={!!data.phone} />
 <Button variant="outline" size="sm" onClick={remove}><Trash2 className="h-3 w-3" /></Button>
 <Button size="sm" onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</Button>
 </div>
 </div>
 </div>
 </header>
 <main className="mx-auto max-w-5xl px-6 py-8 space-y-8">
 <Card title="Status & visibility">
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <Toggle label="Active (publicly listed)" checked={!!data.is_active} onChange={(v) => set("is_active", v)} />
 <Toggle label="Featured" checked={!!data.is_featured} onChange={(v) => set("is_featured", v)} />
 <Toggle label="Verified badge" checked={!!data.verified_badge} onChange={(v) => set("verified_badge", v)} />
 <Toggle label="Soft deleted" checked={!!data.is_deleted} onChange={(v) => set("is_deleted", v)} />
 <div>
 <Label className="text-xs">Listing status</Label>
 <select value={data.listing_status} onChange={(e) => set("listing_status", e.target.value)} className="mt-1 h-9 w-full border border-input bg-background px-2 text-sm">
 {["unclaimed", "claimed_free", "claimed", "premium", "featured"].map((s) => <option key={s} value={s}>{s}</option>)}
 </select>
 </div>
 <div>
 <Label className="text-xs">Plan</Label>
 <select value={data.plan} onChange={(e) => set("plan", e.target.value)} className="mt-1 h-9 w-full border border-input bg-background px-2 text-sm">
 {["free", "premium_monthly", "premium_yearly"].map((s) => <option key={s} value={s}>{s}</option>)}
 </select>
 </div>
 <div>
 <Label className="text-xs">Moderation</Label>
 <select value={data.moderation_status} onChange={(e) => set("moderation_status", e.target.value)} className="mt-1 h-9 w-full border border-input bg-background px-2 text-sm">
 {["pending", "approved", "rejected"].map((s) => <option key={s} value={s}>{s}</option>)}
 </select>
 </div>
 </div>
 </Card>

 <Card title="Basics">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <F label="Doctor name"><Input value={data.name ?? ""} onChange={(e) => set("name", e.target.value)} /></F>
 <F label="Practice"><Input value={data.practice ?? ""} onChange={(e) => set("practice", e.target.value)} /></F>
 <F label="Specialty"><SpecialtySelect value={data.specialty ?? ""} onChange={(v) => set("specialty", v)} /></F>
 <F label="Phone"><Input value={data.phone ?? ""} onChange={(e) => set("phone", e.target.value)} /></F>
 <F label="Email"><Input type="email" value={data.email ?? ""} onChange={(e) => set("email", e.target.value)} /></F>
 <F label="Website"><Input value={data.website ?? ""} onChange={(e) => set("website", e.target.value)} /></F>
 
 <F label="Logo URL"><Input value={data.logo_url ?? ""} onChange={(e) => set("logo_url", e.target.value)} /></F>
 <F label="Hours"><Input value={data.hours ?? ""} onChange={(e) => set("hours", e.target.value)} /></F>
 <F label="Availability"><Input value={data.availability ?? ""} onChange={(e) => set("availability", e.target.value)} /></F>
 </div>
 </Card>

 <Card title="Provider identity & credentials">
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <F label="First name"><Input value={data.first_name ?? ""} onChange={(e) => set("first_name", e.target.value)} /></F>
 <F label="Middle name"><Input value={data.middle_name ?? ""} onChange={(e) => set("middle_name", e.target.value)} /></F>
 <F label="Last name"><Input value={data.last_name ?? ""} onChange={(e) => set("last_name", e.target.value)} /></F>
 <F label="Credentials"><Input value={data.credentials ?? ""} onChange={(e) => set("credentials", e.target.value)} placeholder="MD, DDS, NP…" /></F>
 <F label="Sex">
 <select
 value={data.gender ?? ""}
 onChange={(e) => set("gender", e.target.value || null)}
 className="h-9 w-full border border-input bg-background px-2 text-sm"
 >
 <option value=""> </option>
 <option value="male">Male</option>
 <option value="female">Female</option>
 </select>
 </F>
 <F label="NPI"><Input value={data.npi ?? ""} onChange={(e) => set("npi", e.target.value)} placeholder="10-digit NPI" /></F>
 <F label="License number"><Input value={data.license_number ?? ""} onChange={(e) => set("license_number", e.target.value)} /></F>
 <F label="License state"><Input value={data.license_state ?? ""} onChange={(e) => set("license_state", e.target.value)} placeholder="e.g. TX" /></F>
 <F label="Fax"><Input value={data.fax ?? ""} onChange={(e) => set("fax", e.target.value)} /></F>
 <F label="ZIP"><Input value={data.zip ?? ""} onChange={(e) => set("zip", e.target.value)} /></F>
 </div>
 </Card>

 <Card title="Location">
 <AddressAutocomplete
 address={data.address ?? ""}
 onPick={({ address, city, state }) => setData((d: any) => ({ ...d, address, city: city ?? d.city, state: state ?? d.state }))}
 />
 <CityZipInput
 city={data.city ?? ""}
 state={data.state ?? ""}
 zip=""
 onChange={({ city, state }) => setData((d: any) => ({ ...d, city, state }))}
 />
 </Card>

 <Card title="Profile content">
 <F label="Short description"><Input value={data.short_description ?? ""} onChange={(e) => set("short_description", e.target.value)} /></F>
 <F label="About"><Textarea rows={5} value={data.about ?? ""} onChange={(e) => set("about", e.target.value)} /></F>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <F label="Services"><MultiTagSelect value={data.services ?? []} onChange={(v) => set("services", v)} options={SERVICES} placeholder="Select services…" /></F>
 <F label="Languages"><MultiTagSelect value={data.languages ?? []} onChange={(v) => set("languages", v)} options={LANGUAGES} placeholder="Select languages…" /></F>
 <F label="Insurance"><MultiTagSelect value={data.insurance ?? []} onChange={(v) => set("insurance", v)} options={INSURANCES.map((i) => i.id)} placeholder="Select insurance…" /></F>
 </div>
 <div>
 <Label className="text-xs">Available days</Label>
 <div className="flex flex-wrap gap-2 mt-2">
 {[{ k: "mon", l: "Mon" }, { k: "tue", l: "Tue" }, { k: "wed", l: "Wed" }, { k: "thu", l: "Thu" }, { k: "fri", l: "Fri" }, { k: "sat", l: "Sat" }, { k: "sun", l: "Sun" }].map((d) => {
 const days = (data.available_days ?? []) as string[];
 const on = days.includes(d.k);
 return (
 <button type="button" key={d.k}
 onClick={() => set("available_days", on ? days.filter((x) => x !== d.k) : [...days, d.k])}
 className={`text-xs px-3 py-1.5 border ${on ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:border-primary/40"}`}>
 {d.l}
 </button>
 );
 })}
 </div>
 </div>
 </Card>

 <Card title="Social & Google">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <F label="Google reviews URL"><Input value={data.google_reviews_url ?? ""} onChange={(e) => set("google_reviews_url", e.target.value)} placeholder="https://g.page/..." /></F>
 <F label="Google place ID"><Input value={data.google_place_id ?? ""} onChange={(e) => set("google_place_id", e.target.value)} /></F>
 <F label="Facebook"><Input value={data.facebook_url ?? ""} onChange={(e) => set("facebook_url", e.target.value)} /></F>
 <F label="Instagram"><Input value={data.instagram_url ?? ""} onChange={(e) => set("instagram_url", e.target.value)} /></F>
 <F label="LinkedIn"><Input value={data.linkedin_url ?? ""} onChange={(e) => set("linkedin_url", e.target.value)} /></F>
 <F label="X / Twitter"><Input value={data.x_url ?? ""} onChange={(e) => set("x_url", e.target.value)} /></F>
 <F label="TikTok"><Input value={data.tiktok_url ?? ""} onChange={(e) => set("tiktok_url", e.target.value)} /></F>
 <F label="YouTube"><Input value={data.youtube_url ?? ""} onChange={(e) => set("youtube_url", e.target.value)} /></F>
 </div>
 </Card>

 <Card title={`Email history (${emails.length})`} icon={<Mail className="h-4 w-4" />}>
 <div className="flex items-center justify-between">
 <p className="text-xs text-muted-foreground">All emails sent to this listing. Verified through our built-in email-verification system before delivery.</p>
 <Button variant="outline" size="sm" onClick={() => exportEmailsCsv(emails, data.practice ?? id)} disabled={!emails.length}>
 <Download className="h-3 w-3" /> Export CSV
 </Button>
 </div>
 {emails.length === 0 ? (
 <p className="text-sm text-muted-foreground">No emails sent for this listing yet.</p>
 ) : (
 <div className="border border-border max-h-[28rem] overflow-auto">
 <table className="w-full text-xs">
 <thead className="bg-muted/40 text-muted-foreground sticky top-0">
 <tr>
 <th className="text-left px-2 py-1.5">Subject / type</th>
 <th className="text-left px-2 py-1.5">Recipient</th>
 <th className="text-left px-2 py-1.5">Status</th>
 <th className="text-left px-2 py-1.5">Sent at</th>
 </tr>
 </thead>
 <tbody>
 {emails.map((e) => (
 <tr key={e.id} className="border-t border-border align-top">
 <td className="px-2 py-1.5">
 <div className="font-medium truncate max-w-[280px]">{e.subject}</div>
 {e.subject !== e.email_type && <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{e.email_type}</div>}
 {e.error && <div className="text-[10px] text-destructive mt-0.5 max-w-[280px] truncate" title={e.error}>{e.error}</div>}
 </td>
 <td className="px-2 py-1.5 text-muted-foreground">{e.email}</td>
 <td className="px-2 py-1.5">
 <span className={`inline-flex items-center gap-1 ${e.status === "sent" ? "text-primary" : e.status === "blocked" || e.status === "suppressed" ? "text-muted-foreground" : "text-destructive"}`}>
 {e.status === "sent" ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
 {e.status}
 </span>
 </td>
 <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">{new Date(e.sent_at).toLocaleString()}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </Card>

 <div className="flex justify-end gap-2">
 <Button variant="outline" asChild><Link to="/dashboard/admin">Cancel</Link></Button>
 <Button onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes</Button>
 </div>
 </main>
 </div>
 );
}

function Card({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
 return (
 <section className="border border-border bg-card p-6 space-y-4">
 <h2 className="font-semibold flex items-center gap-2 text-base">{icon}{title}</h2>
 {children}
 </section>
 );
}
function F({ label, children }: { label: string; children: React.ReactNode }) {
 return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
}
function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
 return (
 <label className="flex items-center justify-between border border-border px-3 py-2 cursor-pointer">
 <span className="text-sm">{label}</span>
 <Switch checked={checked} onCheckedChange={onChange} />
 </label>
 );
}

function SendWhatsAppButton({ listingId, hasPhone }: { listingId: string; hasPhone: boolean }) {
 const send = useServerFn(sendClaimWhatsApp);
 const [busy, setBusy] = useState(false);
 const onClick = async () => {
 if (!hasPhone) { toast.error("This listing has no phone number"); return; }
 setBusy(true);
 try {
 const r: any = await send({ data: { listingId, force: true } });
 if (r?.ok) toast.success(`WhatsApp sent (${r.sid})`);
 else toast.error(`WhatsApp not sent: ${r?.error ?? "unknown error"}`);
 } catch (e: any) {
 toast.error(e?.message ?? "Failed to send WhatsApp");
 } finally {
 setBusy(false);
 }
 };
 return (
 <Button variant="outline" size="sm" onClick={onClick} disabled={busy} title="Send claim invite via WhatsApp">
 {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageCircle className="h-3 w-3" />} WhatsApp claim
 </Button>
 );
}
